/**
 * Read-back audio from the relay, assembled in order and turned into something
 * an AudioContext can play. Pure: no audio API, no socket, no clock of its own.
 *
 * The relay streams one `voice:speak` message per Inworld chunk —
 * `{ turnId, seq, pcm }` with `seq` from 1 — then `{ turnId, seq, done, chars,
 * ms }`, or a single `{ turnId, seq: 0, unavailable, reason }` instead of any
 * audio. socket.io delivers messages in order on one connection, but the
 * assembler does not rely on that: it buffers anything early, drops anything
 * duplicated, and releases frames only in sequence, because a frame played out
 * of order is a sentence with its words shuffled.
 *
 * Samples are Int16 LE mono at 24 kHz, the same rate the microphone graph runs
 * at, which is why they can be scheduled on the session's existing context.
 */

export const SAMPLE_RATE_HZ = 24000;
export const BYTES_PER_SAMPLE = 2;
const BYTES_PER_MS = (SAMPLE_RATE_HZ * BYTES_PER_SAMPLE) / 1000; // 48

/** Milliseconds of audio in a byte count of 24 kHz Int16 mono. */
export function pcmDurationMs(byteLength) {
  const bytes = Number(byteLength);
  if (!Number.isFinite(bytes) || bytes <= 0) return 0;
  return bytes / BYTES_PER_MS;
}

/**
 * Whatever the transport handed over — an ArrayBuffer, a Node-style Buffer
 * view, a typed array — as an Int16Array. A view whose byte offset is odd
 * cannot alias an Int16Array, so it is copied; everything else is viewed.
 */
export function toInt16(pcm) {
  if (!pcm) return new Int16Array(0);
  if (pcm instanceof Int16Array) return pcm;
  if (pcm instanceof ArrayBuffer) {
    return new Int16Array(pcm, 0, Math.floor(pcm.byteLength / 2));
  }
  if (ArrayBuffer.isView(pcm)) {
    const length = Math.floor(pcm.byteLength / 2);
    if (pcm.byteOffset % 2 === 0) {
      return new Int16Array(pcm.buffer, pcm.byteOffset, length);
    }
    const copy = new Uint8Array(pcm.buffer.slice(pcm.byteOffset, pcm.byteOffset + length * 2));
    return new Int16Array(copy.buffer);
  }
  return new Int16Array(0);
}

/** Int16 samples to the [-1, 1] floats an AudioBuffer holds. */
export function int16ToFloat32(int16) {
  const out = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i++) out[i] = int16[i] / 0x8000;
  return out;
}

/**
 * Back-to-back start times for a list of frames, so chunks play as one
 * continuous sentence. `startAt` is the context time of the first frame.
 *
 * @returns {Array<{at:number, durationS:number}>}
 */
export function schedulePlan(frames, startAt, sampleRate = SAMPLE_RATE_HZ) {
  let at = Number.isFinite(startAt) ? startAt : 0;
  const rate = Number(sampleRate) > 0 ? Number(sampleRate) : SAMPLE_RATE_HZ;
  return (frames || []).map((frame) => {
    const durationS = (frame ? frame.length : 0) / rate;
    const entry = { at, durationS };
    at += durationS;
    return entry;
  });
}

/**
 * Slack on every end-of-speech timer, in milliseconds. `onended` is the primary
 * signal; the timer exists only to notice that it never fired, so it must land
 * after the audio, never on top of it.
 */
export const SPEAK_END_GRACE_MS = 30;

/**
 * What the end-of-speech backstop should do, given the audio clock.
 *
 * The backstop is a WALL-CLOCK timer aimed at a CONTEXT-CLOCK deadline, and the
 * two run at the same rate only while the context is running. A context that is
 * suspended and resumed — a backgrounded tab, an interrupting phone call, an
 * autoplay policy taking the graph away for a moment — leaves the audio clock
 * behind, so the timer fires while the sentence is still playing.
 *
 * It used to read "the clock advanced at all" as "the audio finished". With
 * audio scheduled to context time 15.52 and the timer firing at 12, that
 * reported `ended`: the microphone hold was released, the dock opened the
 * confirm window, and the remaining three and a half seconds of the machine's
 * own voice went into the microphone stream to be transcribed as if the user
 * had said them.
 *
 * Three answers, and the clock decides which:
 *
 *   `unavailable`  the context is gone or not running, or its clock has not
 *                  moved since the first frame was scheduled — nothing played,
 *                  and the caller says the sentence in the browser voice.
 *   `rearm`        the clock moved but has not reached the end of the last
 *                  scheduled frame: audio is still playing. Wait out the
 *                  remainder and ask again.
 *   `ended`        the clock is at or past the end of the last frame.
 *
 * @param {{state?: string, currentTime?: number, startedCtxTime?: number,
 *   nextStartAt?: number}} clock
 * @returns {'ended'|'rearm'|'unavailable'}
 */
export function speakEndDecision({
  state,
  currentTime,
  startedCtxTime,
  nextStartAt,
} = {}) {
  if (state !== "running") return "unavailable";

  const now = Number(currentTime);
  const started = Number(startedCtxTime);
  if (!Number.isFinite(now) || !Number.isFinite(started)) return "unavailable";
  // A clock that has not moved since the first frame was scheduled played
  // nothing, however long the wall clock waited.
  if (!(now > started)) return "unavailable";

  const end = Number(nextStartAt);
  if (Number.isFinite(end) && now < end) return "rearm";
  return "ended";
}

/**
 * How long to wait before asking again, for a `rearm`. The remaining audio plus
 * the same slack the first arming used.
 */
export function speakEndRearmMs(currentTime, nextStartAt) {
  const now = Number(currentTime);
  const end = Number(nextStartAt);
  if (!Number.isFinite(now) || !Number.isFinite(end)) return SPEAK_END_GRACE_MS;
  return Math.max(0, (end - now) * 1000) + SPEAK_END_GRACE_MS;
}

const keyOf = (turnId) =>
  turnId === undefined || turnId === null ? "__unkeyed__" : String(turnId);

/**
 * One read-back turn's chunks, released in order.
 *
 * `push(msg)` returns what the caller should do now: the frames that became
 * playable (in order), whether the turn is done, and whether the sentence was
 * cut short (`partial`) or never started (`unavailable`).
 */
export function createChunkAssembler(turnId) {
  const key = keyOf(turnId);
  const early = new Map(); // seq -> Int16Array, waiting for its predecessors
  const seen = new Set();
  let next = 1;
  let done = false;
  let doneSeq = null;
  let partial = false;
  let unavailable = false;
  let reason = null;
  let chars = 0;
  let ms = 0;

  const release = () => {
    const frames = [];
    while (early.has(next)) {
      frames.push(early.get(next));
      early.delete(next);
      next += 1;
    }
    return frames;
  };

  const result = (frames) => ({
    frames,
    done,
    partial,
    chars,
    ms,
    unavailable,
    reason,
  });

  return {
    turnId: key,

    push(msg) {
      const body = msg || {};
      if (keyOf(body.turnId) !== key) return result([]);
      if (unavailable || (done && next >= doneSeq)) return result([]);

      if (body.unavailable) {
        unavailable = true;
        reason = body.reason || "upstream";
        early.clear();
        return result([]);
      }

      if (body.done) {
        done = true;
        doneSeq = Number.isFinite(body.seq) ? body.seq : next;
        chars = Number(body.chars) || 0;
        ms = Number(body.ms) || 0;
        const frames = release();
        // The relay says the sentence was cut, or a chunk never arrived before
        // the terminator: either way what plays is not the whole sentence.
        partial = !!body.partial || next < doneSeq;
        return result(frames);
      }

      const seq = Number(body.seq);
      if (!Number.isFinite(seq) || seq < 1 || seen.has(seq) || seq < next) {
        return result([]);
      }
      seen.add(seq);
      early.set(seq, toInt16(body.pcm));
      return result(release());
    },

    isComplete() {
      return unavailable || (done && next >= doneSeq);
    },

    pending() {
      return early.size;
    },
  };
}
